import { useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { registerSchema } from '@authsemply/shared';
import { AuthCard } from '../components/AuthCard';
import { Field } from '../components/Field';
import { NouveauMotDePasse } from '../components/NouveauMotDePasse';
import { Alert } from '../components/Alert';
import { SocialButtons } from '../components/SocialButtons';
import { TermsLabel } from '../components/TermsLabel';
import { api, ApiError, resumeAuthorizeUrl } from '../lib/api';
import { messageOuverture, useOuverture } from '../lib/ouverture';

export function RegisterPage() {
  const [searchParams] = useSearchParams();
  const flowParams = searchParams.toString();
  const returnTo = resumeAuthorizeUrl(searchParams);
  // Inscription SUR INVITATION : le jeton voyage dans l'URL depuis l'écran
  // d'invitation. Le compte rejoindra l'espace qui l'a invité au lieu d'en
  // recevoir un à lui — c'est le serveur qui le fait, dans la même transaction
  // que la création du compte.
  const invitationToken = searchParams.get('invitation') ?? undefined;
  const emailInvite = searchParams.get('email') ?? undefined;
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [emailInscrit, setEmailInscrit] = useState('');
  const [pending, setPending] = useState(false);
  // Invitation adressée à un compte EXISTANT (12/10/2026) : rien n'est créé,
  // il faut se connecter avec ce compte puis accepter l'invitation.
  const [compteExistant, setCompteExistant] = useState(false);
  // Ouverture de la suite (27/09/2026) : inscriptions ouvertes, sur code
  // d'invitation, ou fermées — réglé au back-office du portail.
  const ouverture = useOuverture();
  const codeRequis = Boolean(ouverture?.invitationRequired && !invitationToken);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setPending(true);
    setError(null);

    const form = new FormData(event.currentTarget);
    const parsed = registerSchema.safeParse({
      email: form.get('email'),
      password: form.get('password'),
      firstName: form.get('firstName') || undefined,
      lastName: form.get('lastName') || undefined,
      acceptTerms: form.get('acceptTerms') === 'on',
      invitationToken,
    });

    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? 'Formulaire invalide');
      setPending(false);
      return;
    }
    if (form.get('password') !== form.get('confirmation')) {
      setError('Les deux saisies du mot de passe ne sont pas identiques.');
      setPending(false);
      return;
    }

    try {
      // `signupCode` hors du schéma partagé : il n'a de sens que pour la règle
      // d'ouverture, appliquée par le serveur avant la création.
      const signupCode = String(form.get('signupCode') ?? '').trim() || undefined;
      await api.post('/auth/register', { ...parsed.data, signupCode });
      setEmailInscrit(parsed.data.email);
      setDone(true);
    } catch (err) {
      // Les deux refus d'invitation méritent leur propre message : « inscription
      // impossible » laisserait quelqu'un ressayer indéfiniment un lien mort.
      const code = err instanceof ApiError ? err.code : undefined;
      if (code === 'email_already_used' && invitationToken) {
        setCompteExistant(true);
        return;
      }
      const refusOuverture = err instanceof ApiError ? messageOuverture(code, err.message) : null;
      setError(
        refusOuverture
          ? refusOuverture
          : code === 'email_already_used'
          ? 'Un compte existe déjà avec cette adresse e-mail. Connectez-vous — si vous en aviez demandé la suppression, vous connecter l’annule.'
          : code === 'INVITATION_EMAIL_MISMATCH'
            ? `Cette invitation a été émise pour ${emailInvite ?? 'une autre adresse'}. Inscrivez-vous avec cette adresse, ou demandez une nouvelle invitation.`
            : code === 'INVITATION_INVALID'
              ? 'Cette invitation est expirée ou a déjà été utilisée. Demandez-en une nouvelle.'
              : 'Inscription impossible pour le moment.',
      );
    } finally {
      setPending(false);
    }
  }

  if (compteExistant && invitationToken) {
    return (
      <AuthCard title="Vous avez déjà un compte">
        <p style={{ fontSize: '0.95rem', lineHeight: 1.5, margin: '0 0 1rem' }}>
          Un compte SEmply existe déjà avec l’adresse <strong>{emailInvite ?? 'invitée'}</strong>.
          Aucun nouveau compte n’a été créé : connectez-vous avec celui-ci, puis acceptez
          l’invitation.
        </p>
        <Link
          className="btn"
          to={`/login?suite=${encodeURIComponent(`/invitation?token=${invitationToken}`)}`}
        >
          Se connecter et rejoindre l’espace
        </Link>
        <div className="links">
          <Link to="/mot-de-passe-oublie">Mot de passe oublié ?</Link>
        </div>
      </AuthCard>
    );
  }

  if (done && invitationToken) {
    // Invitation : son lien est arrivé dans cette boîte, l'adresse est prouvée
    // et le compte ouvert tout de suite (27/09/2026).
    return (
      <AuthCard title="Compte créé">
        <Alert message="Votre compte est prêt : connectez-vous pour rejoindre l'espace qui vous a invité." variant="success" />
        <Link className="btn" to={`/login${flowParams ? `?${flowParams}` : ''}`}>
          Se connecter
        </Link>
      </AuthCard>
    );
  }

  if (done) {
    // Sans invitation, le compte reste fermé jusqu'au clic sur le lien.
    return (
      <AuthCard title="Vérifiez votre boîte mail">
        <Alert
          message="Un lien de confirmation vous a été envoyé. Votre compte s'ouvrira dès que vous l'aurez cliqué."
          variant="success"
        />
        <Link className="btn btn-secondary" to="/login">
          Retour à la connexion
        </Link>
        <div className="links">
          <Link to={`/confirmer-adresse?${new URLSearchParams({ email: emailInscrit })}`}>
            Rien reçu ? Recevoir un nouveau lien
          </Link>
        </div>
      </AuthCard>
    );
  }

  if (ouverture && !ouverture.signupOpen) {
    return (
      <AuthCard title="Inscriptions fermées">
        <Alert message={ouverture.signupClosedMessage || 'Les inscriptions sont fermées pour le moment.'} />
        <p className="subtitle">Vous avez déjà un compte ? Vous pouvez vous connecter normalement.</p>
        <Link className="btn btn-secondary" to={`/login${flowParams ? `?${flowParams}` : ''}`}>
          Se connecter
        </Link>
      </AuthCard>
    );
  }

  return (
    <AuthCard title="Créer un compte" subtitle="Valable sur l'ensemble des outils SEmply.">
      <Alert message={error} />
      <form onSubmit={handleSubmit}>
        <Field label="Prénom" name="firstName" autoComplete="given-name" />
        <Field label="Nom" name="lastName" autoComplete="family-name" />
        {/* Sur invitation, l'adresse est IMPOSÉE : le serveur refuse toute
            autre, et un champ modifiable inviterait à la corriger pour se voir
            refuser après coup. */}
        <Field
          label="Adresse e-mail"
          name="email"
          type="email"
          autoComplete="email"
          required
          defaultValue={emailInvite}
          readOnly={Boolean(invitationToken && emailInvite)}
        />
        <NouveauMotDePasse />
        {codeRequis ? (
          <Field
            label="Code d'invitation"
            name="signupCode"
            autoComplete="off"
            required
            placeholder="Reçu par e-mail ou lors d'un événement"
          />
        ) : null}
        <label style={{ fontSize: '0.85rem', display: 'block', marginBottom: '1rem' }}>
          <input type="checkbox" name="acceptTerms" required /> <TermsLabel />
        </label>
        <button className="btn" type="submit" disabled={pending}>
          {pending ? 'Création…' : 'Créer mon compte'}
        </button>
      </form>

      <SocialButtons mode="register" returnTo={returnTo} />

      <div className="links">
        <Link to={`/login${flowParams ? `?${flowParams}` : ''}`}>
          J'ai déjà un compte
        </Link>
      </div>
    </AuthCard>
  );
}
